export interface OrderActivityLogUser {
  name: string;
  role: string;
  /** AB#21907 — the real person, when the entry was written while impersonating this account. */
  via?: string;
}

/**
 * Stable discriminator for detail rendering and future API payloads.
 * Optional on list/detail rows until the backend sends it everywhere.
 */
export type OrderActivityKind =
  | 'factory_assigned'
  | 'factory_edited'
  | 'asana_template'
  | 'activity_flow'
  | 'sample_upload'
  | 'sample_status'
  | 'customer_sample_action'
  | 'sample_rejected_reupload'
  | 'third_party_approval'
  | 'put_in_production'
  | 'production_date_change'
  | 'shipping_details'
  | 'shipping_address_my_task'
  | 'qc_tracking_link'
  | 'dropdown_status'
  | 'reorder'
  | 'delivery_box'
  | 'details_tab_update'
  | 'order_admin_assigned'
  | 'order_copied_without_admin'
  | 'urgency_updated'
  | 'custom_pdf'
  | 'internal_comment'
  | 'price_breakdown'
  | 'tech_pack'
  /** Generic: sample/artwork approval imagery */
  | 'approval'
  /** Generic: scheduling / ETA */
  | 'schedule'
  /** DQC — extend payload when product defines fields */
  | 'dqc_status';

/** Human-readable labels for activity kind filter dropdown; keep in sync when adding `OrderActivityKind` values. */
export const ORDER_ACTIVITY_KIND_LABELS: Record<OrderActivityKind, string> = {
  factory_assigned: 'Factory assigned',
  factory_edited: 'Factory edited',
  asana_template: 'Asana template',
  activity_flow: 'Activity flow',
  sample_upload: 'Sample upload',
  sample_status: 'Sample status',
  customer_sample_action: 'Customer sample action',
  sample_rejected_reupload: 'Sample re-upload after reject',
  third_party_approval: 'Third-party approval',
  put_in_production: 'Put in production',
  production_date_change: 'Production date change',
  shipping_details: 'Shipping details',
  shipping_address_my_task: 'Shipping address (My Task)',
  qc_tracking_link: 'QC tracking link',
  dropdown_status: 'Dropdown / status',
  reorder: 'Reorder',
  delivery_box: 'Delivery box',
  details_tab_update: 'Details tab update',
  order_admin_assigned: 'Order admin assigned',
  order_copied_without_admin: 'Order copied without Order Admin',
  urgency_updated: 'Urgency updated',
  custom_pdf: 'Custom PDF',
  internal_comment: 'Internal comment',
  price_breakdown: 'Price breakdown',
  tech_pack: 'Tech pack',
  approval: 'Approval',
  schedule: 'Schedule',
  dqc_status: 'DQC status',
};

/** Display order for the status filter dropdown (after “All”). */
const ORDER_ACTIVITY_KIND_DISPLAY_ORDER = [
  'tech_pack',
  'approval',
  'schedule',
  'factory_assigned',
  'factory_edited',
  'asana_template',
  'activity_flow',
  'sample_upload',
  'sample_status',
  'customer_sample_action',
  'sample_rejected_reupload',
  'third_party_approval',
  'put_in_production',
  'production_date_change',
  'shipping_details',
  'shipping_address_my_task',
  'qc_tracking_link',
  'dropdown_status',
  'reorder',
  'delivery_box',
  'details_tab_update',
  'order_admin_assigned',
  'order_copied_without_admin',
  'urgency_updated',
  'custom_pdf',
  'internal_comment',
  'price_breakdown',
  'dqc_status',
] as const satisfies readonly OrderActivityKind[];

type _MissingActivityKindInOrder = Exclude<OrderActivityKind, (typeof ORDER_ACTIVITY_KIND_DISPLAY_ORDER)[number]>;
type _AssertAllActivityKindsInDropdownOrder = [_MissingActivityKindInOrder] extends [never] ? true : never;
const _activityKindsExhaustive: _AssertAllActivityKindsInDropdownOrder = true;
void _activityKindsExhaustive;

export const ORDER_ACTIVITY_KIND_OPTIONS: { value: OrderActivityKind; label: string }[] =
  ORDER_ACTIVITY_KIND_DISPLAY_ORDER.map((value) => ({
    value,
    label: ORDER_ACTIVITY_KIND_LABELS[value],
  }));

export interface OrderActivityLogListItem {
  id: string;
  title: string;
  description: string;
  occurred_at: string;
  user: OrderActivityLogUser;
  /** Short badge label in the list (human-readable). */
  activity_type?: string;
  activity_kind?: OrderActivityKind;
  /** Raw backend `action_type` (enum value or order item status). Useful for debugging / future filters. */
  action_type?: string;
  /** Raw backend `activity_meta_data.activity_source` value, when present. */
  activity_source?: string;
}

export interface OrderActivityFieldChange {
  field_label: string;
  previous_value: string;
  new_value: string;
}

export interface OrderActivityPdfChange {
  label?: string;
  previous_url: string;
  new_url: string;
}

export interface OrderActivityImageRef {
  url: string;
  label?: string;
}

export interface OrderActivityStateEntry {
  label: string;
  value: string;
}

export interface OrderActivityMetadataLine {
  label: string;
  value: string;
}

export interface OrderActivityFactoryChange {
  previous_name: string;
  new_name: string;
}

export interface OrderActivityTrackingLink {
  action: 'added' | 'edited' | 'deleted';
  previous_url?: string;
  new_url?: string;
}

export interface OrderActivityReorderInfo {
  original_order_id: number;
  original_order_no: string;
}

/** Dimensions for custom packaging boxes logged from the delivery module */
export interface OrderActivityCustomBoxDimensions {
  width: string | number;
  height: string | number;
  length: string | number;
  weight: string | number;
}

export interface OrderActivityNewCustomBox {
  label: string;
  dimensions: OrderActivityCustomBoxDimensions;
}

/** Maps a delivery note to its packaging box (expanded `delivery_note_created` payload). */
export interface OrderActivityDeliveryNoteBoxRow {
  box_label: string;
  delivery_note_id: number;
  packaging_box_id: string;
  packaging_box_label: string;
}

export interface OrderActivityDeliveryBox {
  box_id: string | number;
  box_name?: string;
  action_label: string;
  /** e.g. `delivery_module` when present on the activity */
  activity_source?: string;
  box_labels?: string[];
  total_items_qty?: number;
  order_line_rows_count?: number;
  not_included_items_qty?: number;
  items_qty_to_ship_total?: number;
  delivery_note_ids?: number[];
  new_custom_boxes?: OrderActivityNewCustomBox[];
  delivery_notes_boxes?: OrderActivityDeliveryNoteBoxRow[];

  /** From `delivery_note_updated` when backend sends extra keys */
  created_delivery_note_ids?: number[];
  deleted_delivery_note_id?: number;
  was_split?: boolean;
}

export interface OrderActivityComment {
  action: 'added' | 'edited' | 'deleted';
  excerpt?: string;
}

export interface OrderActivityShippingSnapshot {
  previous_summary: string;
  new_summary: string;
}

/** Per–order-item factory tech pack PDF paths from `techpack_pdf_urls_updated` activity */
export interface OrderActivityFactoryTechpackPdfRow {
  order_item_id: number;
  generation_pending?: boolean;
  /** Raw storage path when present */
  previous_pdf_url?: string;
  new_pdf_url?: string;
  /** Resolved download URL when `VITE_APP_FILE_URL` is set */
  previous_pdf_href?: string;
  new_pdf_href?: string;
}

/** Merchant + factory tech pack PDF URL update (`techpack_pdf_urls_updated`) */
export interface OrderActivityTechpackPdfUrlsUpdate {
  /** Context label (humanized from backend `comparison_note`, when present) */
  comparison_note?: string;
  /** Order-level merchant PDF regeneration still pending */
  generation_pending?: boolean;
  merchant_generation_pending?: boolean;
  previous_merchant_pdf_url?: string;
  new_merchant_pdf_url?: string;
  previous_merchant_pdf_href?: string;
  new_merchant_pdf_href?: string;
  factory_rows: OrderActivityFactoryTechpackPdfRow[];
  /** From `techpack_edited` · `activity_meta_data.order_item_ids_updated` */
  order_item_ids_updated?: number[];
}

/** Simple read-only table for array meta (samples, third-party decisions, etc.) */
export interface OrderActivityDetailTable {
  title: string;
  columns: string[];
  /** One row per line item; each cell is plain text or "—" */
  rows: string[][];
}

/** Optional QC report link for `quality_control` when `pdf_link` is set in meta */
export interface OrderActivityQcPdfReport {
  url: string;
  href: string;
}

/** Snapshot inside `price_updated` meta (`previous_price_info` / `updated_price_value`) */
export interface OrderActivityPriceBreakdownSnapshot {
  discount?: number;
  is_fixed?: boolean;
  shipping?: number;
  fixed_value?: number;
  total_price?: number;
  total_quantity?: number;
  order_item_final_price?: number;
  /** Present on some updated payloads */
  order_final_price?: number;
  total_final_price?: number;
}

/** Structured price update (`action_type` === `price_updated`) */
export interface OrderActivityPriceUpdateDetail {
  previous: OrderActivityPriceBreakdownSnapshot;
  updated: OrderActivityPriceBreakdownSnapshot;
}

/** One product/sample row from `activity_meta_data.activity_items` (customer portal / submitted for review) */
export interface OrderActivitySamplePreviewCard {
  /** "Approved" | "Rejected" | "Submitted" — human-readable */
  outcome_label?: string;
  product_name?: string;
  /** Customer or admin note for this sample row */
  customer_note?: string;
  preview_images: OrderActivityImageRef[];
}

/**
 * Detail payload from GET …/activity-logs/{id}. All extension fields optional for backward compatibility.
 */
export interface OrderActivityLogDetail extends OrderActivityLogListItem {
  field_changes?: OrderActivityFieldChange[];
  pdf_changes?: OrderActivityPdfChange[];
  approved_images?: OrderActivityImageRef[];
  rejected_images?: OrderActivityImageRef[];
  previous_state?: OrderActivityStateEntry[];
  new_state?: OrderActivityStateEntry[];

  /** Extra key/value rows (Asana, template name, etc.) */
  metadata_lines?: OrderActivityMetadataLine[];
  /** Factory assignment or change */
  factory?: OrderActivityFactoryChange;
  /** Production date change: whether customer was notified */
  notify_customer?: boolean;
  /** Address or shipping summary before/after */
  shipping_snapshot?: OrderActivityShippingSnapshot;
  /** QC / order tracking URL */
  tracking_link?: OrderActivityTrackingLink;
  /** Status move / dropdown reason */
  reason?: string;
  /** Reorder from pane */
  reorder?: OrderActivityReorderInfo;
  /** Delivery box module */
  delivery_box?: OrderActivityDeliveryBox;
  /** Internal comment added/edited/deleted */
  comment?: OrderActivityComment;
  /** Tech pack merchant/factory PDF URL changes */
  techpack_pdf_urls?: OrderActivityTechpackPdfUrlsUpdate;

  /** Structured rows from arrays in `activity_meta_data` */
  detail_tables?: OrderActivityDetailTable[];

  /** QA report PDF when backend sends `pdf_link` */
  qc_pdf_report?: OrderActivityQcPdfReport;

  /** Rich sample previews from `activity_items` (customer portal approve/reject, submitted for review) */
  customer_sample_cards?: OrderActivitySamplePreviewCard[];

  /** Price breakdown before/after (`price_updated`) */
  price_update?: OrderActivityPriceUpdateDetail;
}
